import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../adminApi';
import AdminLayout from './AdminLayout';
import { STATUS_MAP, STATUS_OPTIONS } from '../../constants/projectStatus';

const fmt = (n) => Number(n || 0).toLocaleString();
const fmtDate = (val) => {
    if (!val) return '—';
    try { return new Date(val).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }); }
    catch { return val; }
};
const fmtDateTime = (val) => {
    if (!val) return '—';
    try { return new Date(val).toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return val; }
};

const INVOICE_STATUS_MAP = {
    paid:        { label: '支払済み', bg: '#dcfce7', color: '#15803d' },
    transferred: { label: '送金済み', bg: '#dcfce7', color: '#15803d' },
    refunded:    { label: '返金済み', bg: '#fee2e2', color: '#b91c1c' },
};

const CARD = { background: '#fff', borderRadius: 8, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e0e4ea' };

const InfoRow = ({ label, value }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 12, paddingBottom: 10 }}>
        <span style={{ fontSize: '0.83rem', color: '#6b7280' }}>{label}</span>
        <span style={{ fontSize: '0.83rem', color: '#1a1a2e' }}>{value || '—'}</span>
    </div>
);

const ArrowLeftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
    </svg>
);

const buildTimeline = (project) => {
    if (!project) return [];
    const events = [];
    const { estimate, invoice, schedule } = project;

    events.push({ title: '案件作成', desc: '顧客が新規案件を登録', time: project.created_at });

    if (project.contractor_id && estimate) {
        events.push({ title: '業者マッチング', desc: `${project.contractor?.company_name || '業者'}とのチャット開始`, time: estimate.created_at ? new Date(new Date(estimate.created_at).getTime() - 86400000).toISOString() : project.created_at });
    }
    if (estimate) {
        events.push({ title: '見積提出', desc: `¥${fmt(estimate.amount)}の見積書を提出`, time: estimate.created_at });
    }
    if (['decided', 'invoiced', 'paid', 'scheduled', 'in_progress', 'completion_notified', 'completed'].includes(project.status) && estimate) {
        events.push({ title: '見積承認', desc: '顧客が見積を承認', time: invoice?.created_at ?? estimate.updated_at });
    }
    if (invoice) {
        events.push({ title: '請求書発行', desc: '業者が請求書を発行', time: invoice.created_at });
    }
    if (invoice?.status === 'paid') {
        events.push({ title: '支払完了', desc: '顧客が代金を支払い', time: invoice.updated_at });
    }
    if (schedule?.start_date) {
        events.push({ title: '工事開始', desc: '現場作業開始', time: schedule.start_date });
    }
    if (invoice?.payout_request?.transferred_at) {
        events.push({ title: '振込完了', desc: '業者への振込が完了', time: invoice.payout_request.transferred_at });
    }

    return events
        .filter(e => e.time)
        .sort((a, b) => new Date(a.time) - new Date(b.time));
};

const AdminProjectDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [applying, setApplying] = useState(false);
    const [completingPayout, setCompletingPayout] = useState(false);
    const [refunding, setRefunding] = useState(false);
    const [markingRefunded, setMarkingRefunded] = useState(false);

    const load = () => {
        api.get(`admin/projects/${id}`)
            .then(r => setProject(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [id]); // eslint-disable-line

    const handleForceStatus = async () => {
        if (!selectedStatus) return;
        const label = STATUS_OPTIONS.find(o => o.value === selectedStatus)?.label;
        if (!window.confirm(`ステータスを「${label}」に強制変更しますか？\n※緊急時のみ使用してください`)) return;
        setApplying(true);
        try {
            await api.post(`admin/projects/${id}/force_status`, { status: selectedStatus });
            setSelectedStatus('');
            load();
        } catch { alert('変更に失敗しました'); }
        finally { setApplying(false); }
    };

    const handlePayoutComplete = async () => {
        const payout = project?.invoice?.payout_request;
        if (!payout) return;
        const contractorName = project?.contractor?.company_name || '業者';
        const bank = payout.bank_name ? `${payout.bank_name} ${payout.branch_name || ''} ${payout.account_type === 'savings' ? '普通' : payout.account_type === 'checking' ? '当座' : ''} ${payout.account_number || ''}`.trim() : '（口座情報未登録）';
        if (!window.confirm(`【振込完了の確認】\n\n振込先：${contractorName}\n振込先口座：${bank}\n振込金額：¥${fmt(payout.net_amount)}\n\n銀行振込が完了していることを確認のうえ、「OK」を押してください。\nステータスが「振込済み」に更新されます。`)) return;
        setCompletingPayout(true);
        try {
            await api.post(`admin/payouts/${payout.id}/complete`);
            load();
        } catch { alert('処理に失敗しました'); }
        finally { setCompletingPayout(false); }
    };

    const handleRefund = async () => {
        const invoice = project?.invoice;
        if (!invoice) return;
        if (!window.confirm('この支払いを返金（売上キャンセル）しますか？\n※ペイジェント手数料は戻らず、運営負担になります\n※業者への精算は中止されます')) return;
        setRefunding(true);
        try {
            await api.post(`admin/invoices/${invoice.id}/refund`);
            load();
        } catch (e) {
            alert(e?.response?.data?.error || '返金に失敗しました');
        } finally {
            setRefunding(false);
        }
    };

    const handleMarkRefunded = async () => {
        const invoice = project?.invoice;
        if (!invoice) return;
        if (!window.confirm('【返金済みとして記録】\n\nペイジェント加盟店管理者サイトで返金操作を完了していますか？\n※このボタンはPaygentへの返金処理は行いません。アプリ上のステータスのみ更新します\n※業者への精算は中止されます\n\nPaygent側での返金操作が済んでいることを確認のうえ、「OK」を押してください。')) return;
        const note = window.prompt('記録用メモ（任意。Paygent側の返金処理番号など）', '');
        if (note === null) return; // キャンセル時は中断
        setMarkingRefunded(true);
        try {
            await api.post(`admin/invoices/${invoice.id}/mark_refunded`, note ? { note } : {});
            load();
        } catch (e) {
            alert(e?.response?.data?.error || '記録に失敗しました');
        } finally {
            setMarkingRefunded(false);
        }
    };

    if (loading) return (
        <AdminLayout>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        </AdminLayout>
    );

    if (!project) return (
        <AdminLayout><div style={{ padding: 32, color: '#6b7280' }}>案件が見つかりません</div></AdminLayout>
    );

    const st = STATUS_MAP[project.status] || { label: project.status, bg: '#f3f4f6', color: '#6b7280' };
    const customer = project.customer;
    const contractor = project.contractor;
    const condition = project.condition;
    const estimate = project.estimate;
    const invoice = project.invoice;
    const payout = invoice?.payout_request;
    const timeline = buildTimeline(project);

    const estimateApproved = ['decided', 'invoiced', 'paid', 'scheduled', 'in_progress', 'completion_notified', 'completed'].includes(project.status);

    return (
        <AdminLayout>
            <div style={{ padding: '32px' }}>

                {/* 戻るボタン */}
                <button
                    onClick={() => navigate('/admin/projects')}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '0.875rem', marginBottom: 24, padding: 0 }}
                >
                    <ArrowLeftIcon /> 案件一覧に戻る
                </button>

                {/* 2カラムレイアウト */}
                <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 24 }}>

                    {/* 左カラム */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                        {/* 案件ヘッダー */}
                        <div style={CARD}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                                <div>
                                    <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 4px' }}>案件 #{project.id}</h2>
                                    <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: 0 }}>作成日時: {fmtDateTime(project.created_at)}</p>
                                </div>
                                <span style={{ padding: '4px 12px', borderRadius: 12, fontSize: '0.82rem', fontWeight: 500, background: st.bg, color: st.color }}>
                                    {st.label}
                                </span>
                            </div>
                        </div>

                        {/* 顧客情報 */}
                        <div style={CARD}>
                            <h3 style={{ fontWeight: 700, color: '#1a1a2e', margin: '0 0 16px', fontSize: '0.95rem' }}>顧客情報</h3>
                            <InfoRow label="氏名" value={customer ? `${customer.last_name} ${customer.first_name}` : null} />
                            <InfoRow label="メール" value={customer?.email} />
                            <InfoRow label="電話番号" value={customer?.phone} />
                            <InfoRow label="現住所" value={customer ? `${customer.prefecture || ''}${customer.city || ''}${customer.address || ''}` : null} />
                            <InfoRow label="工事先住所" value={condition ? `${condition.location_prefecture || ''}${condition.location_city || ''}${condition.site_address || ''}${condition.site_building ? ' ' + condition.site_building : ''}` : null} />
                        </div>

                        {/* 業者情報 */}
                        <div style={CARD}>
                            <h3 style={{ fontWeight: 700, color: '#1a1a2e', margin: '0 0 16px', fontSize: '0.95rem' }}>業者情報</h3>
                            <InfoRow label="会社名" value={contractor?.company_name} />
                            <InfoRow label="メール" value={contractor?.email} />
                            <InfoRow label="電話番号" value={contractor?.phone} />
                            <InfoRow label="建設業許可番号" value={contractor?.construction_license_number} />
                            <InfoRow label="インボイス登録番号" value={contractor?.invoice_registration_number} />
                            {contractor?.bank_name && (
                                <div style={{ borderTop: '1px solid #e0e4ea', paddingTop: 14, marginTop: 4 }}>
                                    <p style={{ fontSize: '0.83rem', fontWeight: 600, color: '#1a1a2e', margin: '0 0 10px' }}>振込先情報</p>
                                    <InfoRow label="銀行名" value={contractor.bank_name} />
                                    <InfoRow label="支店名" value={contractor.branch_name} />
                                    <InfoRow label="口座種別" value={contractor.account_type} />
                                    <InfoRow label="口座番号" value={contractor.account_number} />
                                    <InfoRow label="口座名義" value={contractor.account_holder} />
                                </div>
                            )}
                        </div>

                        {/* タイムライン */}
                        <div style={CARD}>
                            <h3 style={{ fontWeight: 700, color: '#1a1a2e', margin: '0 0 20px', fontSize: '0.95rem' }}>タイムライン</h3>
                            {timeline.length === 0 ? (
                                <p style={{ color: '#9ca3af', fontSize: '0.83rem' }}>イベントなし</p>
                            ) : (
                                <div style={{ position: 'relative' }}>
                                    {timeline.map((ev, i) => (
                                        <div key={i} style={{ display: 'flex', gap: 16, paddingBottom: i < timeline.length - 1 ? 24 : 0, position: 'relative' }}>
                                            {/* 縦線 */}
                                            {i < timeline.length - 1 && (
                                                <div style={{ position: 'absolute', left: 7, top: 18, bottom: 0, width: 2, background: '#e0e4ea' }} />
                                            )}
                                            {/* ドット */}
                                            <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#005c53', flexShrink: 0, marginTop: 2, zIndex: 1 }} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1a1a2e' }}>{ev.title}</span>
                                                    <span style={{ fontSize: '0.78rem', color: '#9ca3af', whiteSpace: 'nowrap', marginLeft: 12 }}>{fmtDateTime(ev.time)}</span>
                                                </div>
                                                <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '2px 0 0' }}>{ev.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 右カラム */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                        {/* 見積書カード */}
                        {estimate ? (
                            <div style={CARD}>
                                <h3 style={{ fontWeight: 700, color: '#1a1a2e', margin: '0 0 14px', fontSize: '0.95rem' }}>見積書</h3>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <span style={{ fontSize: '0.83rem', color: '#6b7280' }}>金額</span>
                                    <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e' }}>¥{fmt(estimate.amount)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <span style={{ fontSize: '0.83rem', color: '#6b7280' }}>提出日</span>
                                    <span style={{ fontSize: '0.83rem', color: '#1a1a2e' }}>{fmtDate(estimate.created_at)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '0.83rem', color: '#6b7280' }}>ステータス</span>
                                    <span style={{ padding: '2px 8px', background: estimate.status === 'rejected' ? '#fee2e2' : estimateApproved ? '#dcfce7' : '#ffedd5', color: estimate.status === 'rejected' ? '#b91c1c' : estimateApproved ? '#15803d' : '#c2410c', borderRadius: 4, fontSize: '0.75rem', fontWeight: 500 }}>
                                        {estimate.status === 'rejected' ? '差し戻し' : estimateApproved ? '承認済み' : '確認待ち'}
                                    </span>
                                </div>
                                <button
                                    onClick={() => navigate(`/customer/estimate/${project.id}`)}
                                    style={{ width: '100%', marginTop: 14, padding: '8px', border: '1px solid #005c53', borderRadius: 4, background: '#fff', color: '#005c53', fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer' }}
                                >
                                    内容を確認
                                </button>
                            </div>
                        ) : (
                            <div style={{ ...CARD, color: '#9ca3af', fontSize: '0.83rem', textAlign: 'center' }}>見積書未提出</div>
                        )}

                        {/* 請求書カード */}
                        {invoice ? (
                            <div style={CARD}>
                                <h3 style={{ fontWeight: 700, color: '#1a1a2e', margin: '0 0 14px', fontSize: '0.95rem' }}>請求書</h3>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <span style={{ fontSize: '0.83rem', color: '#6b7280' }}>金額</span>
                                    <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e' }}>¥{fmt(invoice.amount)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <span style={{ fontSize: '0.83rem', color: '#6b7280' }}>支払期日</span>
                                    <span style={{ fontSize: '0.83rem', color: '#1a1a2e' }}>{fmtDate(invoice.due_date)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '0.83rem', color: '#6b7280' }}>ステータス</span>
                                    {(() => {
                                        const ist = INVOICE_STATUS_MAP[invoice.status] || { label: '未払い', bg: '#fff3e0', color: '#f57c00' };
                                        return (
                                            <span style={{ padding: '2px 8px', background: ist.bg, color: ist.color, borderRadius: 4, fontSize: '0.75rem', fontWeight: 500 }}>
                                                {ist.label}
                                            </span>
                                        );
                                    })()}
                                </div>
                                {invoice.status === 'refunded' && invoice.note && (
                                    <div style={{ marginTop: 10, padding: '8px 10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'pre-wrap' }}>
                                        {invoice.note}
                                    </div>
                                )}
                                <button
                                    onClick={() => navigate(`/customer/invoice/${project.id}`)}
                                    style={{ width: '100%', marginTop: 14, padding: '8px', border: '1px solid #f59e0b', borderRadius: 4, background: '#fff', color: '#f59e0b', fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer' }}
                                >
                                    内容を確認
                                </button>
                                {['paid', 'transferred'].includes(invoice.status) && (!payout || payout.status !== 'completed') && (
                                    <button
                                        onClick={handleRefund}
                                        disabled={refunding}
                                        style={{ width: '100%', marginTop: 8, padding: '8px', border: '1px solid #dc2626', borderRadius: 4, background: '#fff', color: refunding ? '#9ca3af' : '#dc2626', fontSize: '0.83rem', fontWeight: 600, cursor: refunding ? 'default' : 'pointer' }}
                                    >
                                        {refunding ? '返金処理中...' : '返金（売上キャンセル）'}
                                    </button>
                                )}
                                {['paid', 'transferred'].includes(invoice.status) && (!payout || payout.status !== 'completed') && (
                                    <button
                                        onClick={handleMarkRefunded}
                                        disabled={markingRefunded}
                                        title="Paygent加盟店管理者サイトで返金操作を済ませた後に押してください（Paygentへの通信は行いません）"
                                        style={{ width: '100%', marginTop: 8, padding: '8px', border: '1px solid #9ca3af', borderRadius: 4, background: '#fff', color: markingRefunded ? '#d1d5db' : '#6b7280', fontSize: '0.83rem', fontWeight: 600, cursor: markingRefunded ? 'default' : 'pointer' }}
                                    >
                                        {markingRefunded ? '記録中...' : '返金済みとして記録（手動）'}
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div style={{ ...CARD, color: '#9ca3af', fontSize: '0.83rem', textAlign: 'center' }}>請求書未発行</div>
                        )}

                        {/* 振込カード */}
                        {payout && (
                            <div style={CARD}>
                                <h3 style={{ fontWeight: 700, color: '#1a1a2e', margin: '0 0 14px', fontSize: '0.95rem' }}>振込情報</h3>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <span style={{ fontSize: '0.83rem', color: '#6b7280' }}>手数料</span>
                                    <span style={{ fontSize: '0.83rem', color: '#1a1a2e' }}>¥{fmt(payout.platform_fee)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <span style={{ fontSize: '0.83rem', color: '#6b7280' }}>振込金額</span>
                                    <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#10b981' }}>¥{fmt(payout.net_amount)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <span style={{ fontSize: '0.83rem', color: '#6b7280' }}>ステータス</span>
                                    <span style={{ padding: '2px 8px', background: payout.status === 'completed' ? '#dcfce7' : '#fff3e0', color: payout.status === 'completed' ? '#15803d' : '#f57c00', borderRadius: 4, fontSize: '0.75rem', fontWeight: 500 }}>
                                        {payout.status === 'completed' ? '振込済み' : '振込待ち'}
                                    </span>
                                </div>
                                {['manual', 'pending', 'failed'].includes(payout.status) && (
                                    <button
                                        onClick={handlePayoutComplete}
                                        disabled={completingPayout}
                                        style={{ width: '100%', padding: '9px', background: completingPayout ? '#9ca3af' : '#10b981', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.83rem', fontWeight: 600, cursor: completingPayout ? 'default' : 'pointer' }}
                                    >
                                        {completingPayout ? '処理中...' : '振込を完了'}
                                    </button>
                                )}
                                {payout.transferred_at && (
                                    <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: '8px 0 0', textAlign: 'right' }}>
                                        振込日: {fmtDate(payout.transferred_at)}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* 管理者操作 */}
                        <div style={CARD}>
                            <h3 style={{ fontWeight: 700, color: '#1a1a2e', margin: '0 0 14px', fontSize: '0.95rem' }}>管理者操作</h3>
                            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#6b7280', marginBottom: 8 }}>
                                ステータス強制変更
                            </label>
                            <select
                                value={selectedStatus}
                                onChange={e => setSelectedStatus(e.target.value)}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e0e4ea', borderRadius: 4, fontSize: '0.875rem', marginBottom: 10, outline: 'none' }}
                            >
                                <option value="">選択してください</option>
                                {STATUS_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                            <button
                                onClick={handleForceStatus}
                                disabled={!selectedStatus || applying}
                                style={{ width: '100%', padding: '9px', background: !selectedStatus || applying ? '#9ca3af' : '#141c3d', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.83rem', fontWeight: 600, cursor: !selectedStatus || applying ? 'default' : 'pointer' }}
                            >
                                {applying ? '適用中...' : '変更を適用'}
                            </button>
                            <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '8px 0 0' }}>※緊急時のみ使用してください</p>
                        </div>
                    </div>
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </AdminLayout>
    );
};

export default AdminProjectDetailPage;
